import { useMemo, useState } from "react";
import { Bookmark, BookmarkPlus, Check, Sparkles, Star, Trash2, Monitor } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { countPresetFields } from "../helpers";
import type { PresetMap } from "../types";

/** Compara dois conjuntos de critérios ignorando a busca textual — usado para destacar o preset ativo. */
const stableCriteria = (criteria: Record<string, any>): string => {
  const { search: _search, ...rest } = criteria || {};
  return JSON.stringify(rest, Object.keys(rest).sort());
};

interface PresetCardProps {
  name: string;
  count: number;
  active: boolean;
  variant: "system" | "user";
  onApply: () => void;
  onDelete?: () => void;
}

function PresetCard({ name, count, active, variant, onApply, onDelete }: PresetCardProps) {
  const Icon = variant === "system" ? Bookmark : Star;

  return (
    <div
      className={cn(
        "group relative flex items-center gap-3 rounded-xl border p-3 transition-all",
        active
          ? "border-primary/40 bg-primary/5 shadow-sm"
          : "border-border bg-card hover:border-primary/30 hover:bg-accent/40",
      )}
    >
      <button
        type="button"
        onClick={onApply}
        className="flex flex-1 items-center gap-3 text-left focus-visible:outline-none"
      >
        <span
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors",
            active
              ? "bg-primary text-primary-foreground"
              : "bg-secondary text-muted-foreground group-hover:text-foreground",
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium text-foreground">{name}</span>
          <span className="text-[11px] text-muted-foreground">
            {count} {count === 1 ? "filtro" : "filtros"}
            {variant === "system" && " · padrão"}
          </span>
        </span>
      </button>

      {active && (
        <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
          <Check className="h-3 w-3" /> Ativo
        </span>
      )}

      {onDelete && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
          onClick={onDelete}
          title={`Excluir preset "${name}"`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

interface PresetSectionProps {
  title: string;
  presets: PresetMap;
  activeKey: string;
  variant: "system" | "user";
  onApply: (criteria: Record<string, any>) => void;
  onDelete?: (name: string) => void;
}

function PresetSection({
  title,
  presets,
  activeKey,
  variant,
  onApply,
  onDelete,
}: PresetSectionProps) {
  const entries = Object.entries(presets);
  if (entries.length === 0) return null;

  return (
    <div className="space-y-2">
      <h4 className="px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h4>
      <div className="space-y-2">
        {entries.map(([name, criteria]) => (
          <PresetCard
            key={`${variant}-${name}`}
            name={name}
            count={countPresetFields(criteria)}
            active={stableCriteria(criteria) === activeKey}
            variant={variant}
            onApply={() => onApply(criteria)}
            onDelete={onDelete ? () => onDelete(name) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

interface PresetsPanelProps {
  /** Presets fixos, definidos no código. */
  systemPresets: PresetMap;
  /** Presets salvos pelo usuário. */
  userPresets: PresetMap;
  /** Habilita salvar/excluir presets do usuário. */
  canPersist: boolean;
  /** Critérios atualmente aplicados — para destacar o preset ativo. */
  appliedCriteria: Record<string, any> | null | undefined;
  /** Nº de filtros ativos no momento (para o resumo do bloco de salvar). */
  activeCount: number;
  onApply: (criteria: Record<string, any>) => void;
  onSave: (name: string) => boolean;
  onDelete: (name: string) => void;
}

/* Aba "Presets": salvar a combinação atual e aplicar/excluir presets existentes. */
export function PresetsPanel({
  systemPresets,
  userPresets,
  canPersist,
  appliedCriteria,
  activeCount,
  onApply,
  onSave,
  onDelete,
}: PresetsPanelProps) {
  const [name, setName] = useState("");
  const activeKey = useMemo(() => stableCriteria(appliedCriteria || {}), [appliedCriteria]);

  const isEmpty = Object.keys(systemPresets).length === 0 && Object.keys(userPresets).length === 0;

  const handleSave = () => {
    if (onSave(name)) setName("");
  };

  return (
    <div className="space-y-6 px-6 py-6">
      {canPersist && (
        <div className="space-y-3">
          <div className="rounded-xl border border-dashed border-border bg-muted/30 p-4">
            <div className="mb-3 flex items-center gap-2">
              <BookmarkPlus className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Salvar combinação atual</span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Nome do preset..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                }}
                className="h-9 text-xs"
              />
              <Button size="sm" onClick={handleSave} disabled={!name.trim()} className="shrink-0">
                Salvar
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {activeCount > 0
                ? `${activeCount} ${activeCount === 1 ? "filtro será salvo" : "filtros serão salvos"} neste preset.`
                : "Aplique filtros para salvá-los como preset."}
            </p>
          </div>

          {/* Aviso de persistência local */}
          <div className="flex items-start gap-2 px-1 text-[11px] text-muted-foreground/80">
            <Monitor className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500/80" />
            <p>
              Os presets personalizados são salvos <strong>apenas neste navegador e máquina</strong>
              . Se trocar de dispositivo ou limpar os dados de navegação, eles serão resetados.
            </p>
          </div>
        </div>
      )}

      {isEmpty ? (
        <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Sparkles className="h-5 w-5" />
          </span>
          <p className="text-sm font-medium text-foreground">Nenhum preset salvo</p>
          <p className="max-w-[15rem] text-xs text-muted-foreground">
            Aplique filtros e salve a combinação para reutilizá-la depois.
          </p>
        </div>
      ) : (
        <>
          <PresetSection
            title="Predefinidos"
            presets={systemPresets}
            activeKey={activeKey}
            variant="system"
            onApply={onApply}
          />
          <PresetSection
            title="Meus presets"
            presets={userPresets}
            activeKey={activeKey}
            variant="user"
            onApply={onApply}
            onDelete={canPersist ? onDelete : undefined}
          />
        </>
      )}
    </div>
  );
}
